import { toast } from "@/components/ToastNotification";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { convertDataToISO, convertISOToData } from "@/lib/utils";
import { Especialidade } from "@/pages/especialidades/interface/Especialidade.interface";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Configuracoes } from "./components/Configuracoes";
import { Endereco as EnderecoComponent } from "./components/Endereco";
import { Medico } from "./components/Medico";
import { Pessoa } from "./components/Pessoa";
import { Usuario } from "./components/Usuario";
import { Empresa, Endereco, PerfilAcesso, TipoUsuario, UsuariosEditProps } from "./interface/Usuario.interface";
import { atualizarUsuarioSchema, CriarUsuarioFormData, criarUsuarioSchema } from "./schemas/usuario.schema";


const UsuariosEdit = ({ open, onOpenChange, usuario, onSuccess }: UsuariosEditProps) => {
  const [medicoData, setMedicoData] = useState({
    crm: "",
    uf_crm: "",
    id_especialidade: "",
    subespecialidades: "",
    status_crm: "ATIVO" as "ATIVO" | "INATIVO" | "SUSPENSO",
    data_validade_crm: "",
    observacoes: "",
    ativo: true
  });
  
  const [loading, setLoading] = useState(false);
  const [perfisAcesso, setPerfisAcesso] = useState<PerfilAcesso[]>([]);
  const [tiposUsuario, setTiposUsuario] = useState<TipoUsuario[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [especialidades, setEspecialidades] = useState<Especialidade[]>([]);
  const [abaAtiva, setAbaAtiva] = useState<string>("pessoa");
  const { user } = useAuth();

  // Inicializa o formulário com react-hook-form
  const form = useForm<CriarUsuarioFormData>({
    resolver: zodResolver(usuario ? atualizarUsuarioSchema : criarUsuarioSchema),
    defaultValues: {
      nome: "",
      tipo: "FISICA",
      categoria: "FUNCIONARIO",
      cpf: "",
      cnpj: "",
      nomeFantasia: "",
      inscricaoEstadual: "",
      rg: "",
      dataNascimento: "",
      genero: undefined,
      email: "",
      username: "",
      senha: "",
      id_perfil_acesso: "",
      id_tipo_usuario: "",
      id_empresa: "",
      ativo: true,
      enderecos: [],
      emails: [],
      telefones: [],
    },
    mode: "onChange",
  });

  const formData = form.watch();
  
  // Verifica se o tipo de usuário selecionado é Médico
  const tipoUsuarioSelecionado = tiposUsuario.find(t => t.id.toString() === formData.id_tipo_usuario);
  const isMedico = tipoUsuarioSelecionado?.codigo === 'MEDICO';

  // Obtém id_empresa do usuário logado ou usa valor padrão
  const getIdEmpresa = (): number => {
    if (user?.id_empresa) {
      return user.id_empresa;
    }
    // Valor padrão igual à seed
    return 1;
  };

  useEffect(() => {
    const carregarDadosAuxiliares = async () => {
      try {
        const [perfisResponse, tiposResponse, empresasResponse, especialidadesResponse] = await Promise.all([
          api.get("/api/perfil-acesso"),
          api.get("/api/tipo-usuario"),
          api.get("/api/empresas"),
          api.get("/api/especialidades")
        ]);
        
        setPerfisAcesso(perfisResponse.result || []);
        setTiposUsuario(tiposResponse.result || []);
        setEmpresas(empresasResponse.result || []);
        
        // Tratamento especial para especialidades (pode vir paginado ou não)
        const especialidadesData = especialidadesResponse.result;
        if (especialidadesData?.data) {
          setEspecialidades(especialidadesData.data);
        } else if (Array.isArray(especialidadesData)) {
          setEspecialidades(especialidadesData);
        } else {
          setEspecialidades([]);
        }
      } catch (error) {
        console.error("Erro ao carregar dados auxiliares:", error);
      }
    };

    const carregarDadosUsuario = async () => {
      if (usuario?.id) {
        try {
          // Busca os dados completos do usuário incluindo médico
          const response = await api.get(`/api/usuario/${usuario.id}`);
          const usuarioCompleto = response.result;
          
          if (usuarioCompleto) {
            // Atualiza o objeto usuario com os dados completos
            const cpfPessoa = usuarioCompleto.pessoa?.pessoaFisica?.cpf || "";
            // Mapeia os endereços do backend para o formato do formulário
            const enderecosFormatados = usuarioCompleto.pessoa?.enderecos?.map((end: any) => ({
              cep: end.cep || "",
              logradouro: end.logradouro || "",
              numero: end.numero || "",
              bairro: end.bairro || "",
              cidade: end.cidade || "",
              uf: end.uf || "",
              complemento: end.complemento || "",
              principal: end.principal || false
            })) || [];

            // Mapeia os telefones do backend para o formato do formulário
            const telefonesFormatados = usuarioCompleto.pessoa?.telefones?.map((tel: any) => ({
              telefone: tel.telefone || "",
              tipo: tel.tipo || "CELULAR",
              principal: tel.principal || false
            })) || [];

            form.reset({
              nome: usuarioCompleto.pessoa?.nome || "",
              tipo: usuarioCompleto.pessoa?.tipo || "FISICA",
              categoria: usuarioCompleto.pessoa?.categoria || "FUNCIONARIO",
              cpf: cpfPessoa,
              cnpj: usuarioCompleto.pessoa?.pessoaJuridica?.cnpj || "",
              nomeFantasia: usuarioCompleto.pessoa?.pessoaJuridica?.nome_fantasia || "",
              inscricaoEstadual: usuarioCompleto.pessoa?.pessoaJuridica?.inscricao_estadual || "",
              rg: usuarioCompleto.pessoa?.pessoaFisica?.rg || "",
              dataNascimento: usuarioCompleto.pessoa?.data_nascimento || usuarioCompleto.pessoa?.dataNascimento || "",
              genero: (usuarioCompleto.pessoa?.genero as "MASCULINO" | "FEMININO" | "OUTRO") || undefined,
              email: usuarioCompleto.email,
              username: usuarioCompleto.username || "",
              senha: "",
              id_perfil_acesso: usuarioCompleto.perfil?.id?.toString() || "",
              id_tipo_usuario: usuarioCompleto.tipoUsuario?.id?.toString() || "",
              id_empresa: "",
              ativo: usuarioCompleto.ativo,
              enderecos: enderecosFormatados,
              emails: [],
              telefones: telefonesFormatados,
            });

            // Carregar dados do médico se existir
            if (usuarioCompleto.pessoa?.medico) {
              const medico = usuarioCompleto.pessoa.medico;
              
              // Converte a data de validade do CRM para o formato DD/MM/YYYY
              let dataValidadeFormatada = "";
              if (medico.data_validade_crm) {
                try {
                  // Se for uma string no formato YYYY-MM-DD, converte
                  if (typeof medico.data_validade_crm === 'string') {
                    // Verifica se já está no formato brasileiro
                    if (medico.data_validade_crm.includes('/')) {
                      dataValidadeFormatada = medico.data_validade_crm;
                    } else {
                      dataValidadeFormatada = convertISOToData(medico.data_validade_crm);
                    }
                  } else {
                    // Se for um objeto Date, converte para string primeiro
                    const dataISO = new Date(medico.data_validade_crm).toISOString().split('T')[0];
                    dataValidadeFormatada = convertISOToData(dataISO);
                  }
                } catch (error) {
                  console.error("Erro ao converter data de validade do CRM:", error);
                  dataValidadeFormatada = "";
                }
              }
              
              const dadosMedico = {
                crm: medico.crm || "",
                uf_crm: medico.uf_crm || "",
                id_especialidade: medico.id_especialidade?.toString() || "",
                subespecialidades: medico.subespecialidades || "",
                status_crm: (medico.status_crm || "ATIVO") as "ATIVO" | "INATIVO" | "SUSPENSO",
                data_validade_crm: dataValidadeFormatada,
                observacoes: medico.observacoes || "",
                ativo: medico.ativo ?? true
              };
              
              setMedicoData(dadosMedico);
            } else {
              // Se não for médico ou não tiver dados do médico, reseta os dados
              setMedicoData({
                crm: "",
                uf_crm: "",
                id_especialidade: "",
                subespecialidades: "",
                status_crm: "ATIVO",
                data_validade_crm: "",
                observacoes: "",
                ativo: true
              });
            }
          }
        } catch (error) {
          console.error("Erro ao carregar dados do usuário:", error);
        }
      }
    };

    if (open) {
      carregarDadosAuxiliares();
      // Reseta a aba para "pessoa" quando o dialog abrir
      setAbaAtiva("pessoa");
      
      // Se estiver editando, carrega os dados completos do usuário
      if (usuario?.id) {
        carregarDadosUsuario();
      }
    }

    // Se não estiver editando (criação de novo usuário), reseta os dados
    if (!usuario?.id) {
      form.reset({
        nome: "",
        tipo: "FISICA",
        categoria: "FUNCIONARIO",
        cpf: "",
        cnpj: "",
        nomeFantasia: "",
        inscricaoEstadual: "",
        rg: "",
        dataNascimento: "",
        genero: undefined,
        email: "",
        username: "",
        senha: "",
        id_perfil_acesso: "",
        id_tipo_usuario: "",
        id_empresa: "",
        ativo: true,
        enderecos: [],
        emails: [],
        telefones: [],
      });

      setMedicoData({
        crm: "",
        uf_crm: "",
        id_especialidade: "",
        subespecialidades: "",
        status_crm: "ATIVO",
        data_validade_crm: "",
        observacoes: "",
        ativo: true
      });
    }
  }, [usuario, open, form]);

  const handleSubmit = async (data: CriarUsuarioFormData) => {
    // Validação: se for médico, verifica se a especialidade foi selecionada
    if (isMedico) {
      if (!medicoData.id_especialidade || medicoData.id_especialidade.trim() === "") {
        toast.error("Por favor, selecione a especialidade do médico antes de salvar.");
        setAbaAtiva("medico"); // Muda para a aba de médico para destacar o campo
        setLoading(false);
        return;
      }
    }

    setLoading(true);
  
    try {
      console.log("🚀 Iniciando salvamento do usuário...", data);

      // 🧩 Monta o payload no formato esperado pela API
      const idEmpresa = getIdEmpresa();
      const payload: any = {
        id_empresa: idEmpresa,
        id_perfil_acesso: data.id_perfil_acesso ? parseInt(data.id_perfil_acesso) : null,
        id_tipo_usuario: data.id_tipo_usuario ? parseInt(data.id_tipo_usuario) : null,
        pessoa: {
          nome: data.nome,
          tipo: data.tipo,
          categoria: data.categoria,
          genero: data.genero || undefined,
          data_nascimento: data.dataNascimento || undefined
        },
  
        // Envia pessoa_fisica ou pessoa_juridica dependendo do tipo
        pessoa_fisica:
          data.tipo === "FISICA"
            ? {
                cpf: data.cpf,
                rg: data.rg || undefined
              }
            : undefined,
  
        pessoa_juridica:
          data.tipo === "JURIDICA"
            ? {
                cnpj: data.cnpj,
                nome_fantasia: data.nomeFantasia || undefined,
                inscricao_estadual: data.inscricaoEstadual || undefined
              }
            : undefined,
  
        usuario: {
          cpf: data.tipo === "FISICA" ? data.cpf : (data.cnpj?.replace(/\D/g, "").slice(0, 11) || ""),
          email: data.email,
          username: data.username || null,
          // Só envia senha se estiver preenchida
          // Na criação: envia senha (obrigatória)
          // Na edição: só envia se o usuário preencheu uma nova senha
          ...(usuario 
            ? (data.senha && data.senha.trim() !== "" ? { senha: data.senha } : {}) 
            : { senha: data.senha || "" }
          ),
          id_perfil_acesso: data.id_perfil_acesso ? parseInt(data.id_perfil_acesso) : null,
          id_tipo_usuario: data.id_tipo_usuario ? parseInt(data.id_tipo_usuario) : null,
          ativo: data.ativo
        },
  
        enderecos:
          data.enderecos?.map((e) => ({ 
            cep: e.cep || undefined,
            logradouro: e.logradouro,
            numero: e.numero || undefined,
            bairro: e.bairro || undefined,
            cidade: e.cidade || undefined,
            uf: e.uf || undefined,
            complemento: e.complemento || undefined,
            principal: e.principal ?? false
          })) || [],
  
        telefones: data.telefones?.length
          ? data.telefones.map((t: any) => ({
              telefone: t.telefone,
              tipo: t.tipo || "CELULAR",
              principal: t.principal ?? false
            }))
          : [],
  
        emails: data.email
          ? [
              {
                email: data.email,
                principal: true
              }
            ]
          : []
      };

      // Se for médico, adiciona os dados do médico ao payload
      if (isMedico && medicoData.crm) {
        // Converte a data de DD/MM/YYYY para YYYY-MM-DD antes de enviar
        let dataValidadeISO: string | undefined = undefined;
        
        if (medicoData.data_validade_crm && medicoData.data_validade_crm.trim() !== '') {
          const dataConvertida = convertDataToISO(medicoData.data_validade_crm);
          // Só inclui a data se a conversão for bem-sucedida
          if (dataConvertida) {
            dataValidadeISO = dataConvertida;
          }
        }
        
        payload.medico = {
          id_empresa: idEmpresa,
          crm: medicoData.crm,
          uf_crm: medicoData.uf_crm,
          id_especialidade: medicoData.id_especialidade ? parseInt(medicoData.id_especialidade) : undefined,
          subespecialidades: medicoData.subespecialidades || undefined,
          status_crm: medicoData.status_crm,
          data_validade_crm: dataValidadeISO,
          observacoes: medicoData.observacoes || undefined,
          ativo: medicoData.ativo
        };
      }
  
      console.log("📤 Payload enviado:", payload);
  
      if (usuario) {
        await api.put(`/api/usuario/${usuario.id}`, payload);
        toast.success("Usuário atualizado com sucesso!");
      } else {
        await api.post("/api/usuario", payload);
        toast.success("Usuário criado com sucesso!");
      }
  
      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error("❌ Erro ao salvar usuário:", error);
      
      // Tratamento melhorado de erros
      let errorMessage = "Erro ao salvar o usuário";
      
      if (error?.response?.data) {
        const errorData = error.response.data;
        if (errorData.details && Array.isArray(errorData.details)) {
          // Erros de validação do Zod
          const validationErrors = errorData.details.map((issue: any) => {
            const path = issue.path?.join(".") || "campo";
            return `${path}: ${issue.message}`;
          }).join(", ");
          errorMessage = `Erros de validação: ${validationErrors}`;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  

  const handleClose = () => {
    onOpenChange(false);
  };

  const adicionarEndereco = () => {
    const currentEnderecos = form.getValues("enderecos") || [];
    form.setValue("enderecos", [
      ...currentEnderecos,
      {
        cep: "",
        logradouro: "",
        numero: "",
        bairro: "",
        cidade: "",
        uf: "",
        complemento: "",
        principal: currentEnderecos.length === 0
      }
    ]);
  };

  const removerEndereco = (index: number) => {
    const currentEnderecos = form.getValues("enderecos") || [];
    const novosEnderecos = currentEnderecos.filter((_, i) => i !== index);
    form.setValue("enderecos", novosEnderecos);
  };

  const atualizarEndereco = (index: number, campo: keyof Endereco, valor: string | boolean) => {
    const currentEnderecos = form.getValues("enderecos") || [];
    const novosEnderecos = [...currentEnderecos];
    novosEnderecos[index] = {
      ...novosEnderecos[index],
      [campo]: valor
    };
    form.setValue("enderecos", novosEnderecos);
  };

  const atualizarEnderecoCompleto = (index: number, campos: Partial<Endereco>) => {
    const currentEnderecos = form.getValues("enderecos") || [];
    const novosEnderecos = [...currentEnderecos];
    novosEnderecos[index] = {
      ...novosEnderecos[index],
      ...campos
    };
    form.setValue("enderecos", novosEnderecos);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {usuario ? "Editar Usuário" : "Novo Usuário"}
          </DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit, (errors) => {
            // Esta função é chamada quando a validação falha
            console.log("❌ Erros de validação:", errors);
            
            // Coleta todos os erros do formulário
            const camposFaltando: string[] = [];

            // Mapeamento de campos para nomes amigáveis
            const mapeamentoCampos: { [key: string]: string } = {
              'nome': 'Nome',
              'cpf': 'CPF',
              'cnpj': 'CNPJ',
              'email': 'Email',
              'username': 'Username',
              'senha': 'Senha',
              'id_perfil_acesso': 'Perfil de Acesso',
              'id_tipo_usuario': 'Tipo de Usuário',
              'enderecos': 'Endereço',
              'cep': 'CEP',
              'logradouro': 'Logradouro',
              'bairro': 'Bairro',
              'cidade': 'Cidade',
              'uf': 'UF',
            };

            // Função auxiliar para formatar o nome do campo
            const formatarNomeCampo = (path: string): string => {
              // Remove índices de array (ex: enderecos.0.cep -> enderecos.cep)
              const pathLimpo = path.replace(/\.\d+\./g, '.').replace(/\.\d+$/, '');
              
              // Extrai o último campo do caminho
              const partes = pathLimpo.split('.');
              const ultimoCampo = partes[partes.length - 1];
              
              // Se for um campo conhecido, retorna o nome amigável
              if (mapeamentoCampos[ultimoCampo]) {
                return mapeamentoCampos[ultimoCampo];
              }
              
              // Se o caminho completo está no mapeamento
              if (mapeamentoCampos[pathLimpo]) {
                return mapeamentoCampos[pathLimpo];
              }
              
              // Retorna o último campo capitalizado
              return ultimoCampo.charAt(0).toUpperCase() + ultimoCampo.slice(1);
            };

            // Função recursiva para coletar todos os erros
            const coletarErros = (obj: any, prefix = ''): void => {
              if (!obj || typeof obj !== 'object') return;

              Object.keys(obj).forEach((key) => {
                const caminhoCompleto = prefix ? `${prefix}.${key}` : key;
                const valor = obj[key];

                if (valor) {
                  if (typeof valor === 'object') {
                    if (valor.message) {
                      // É um erro com mensagem
                      const nomeCampo = formatarNomeCampo(caminhoCompleto);
                      if (!camposFaltando.includes(nomeCampo)) {
                        camposFaltando.push(nomeCampo);
                      }
                    } else if (Array.isArray(valor)) {
                      // É um array de erros
                      valor.forEach((item, index) => {
                        if (item && typeof item === 'object') {
                          coletarErros(item, `${caminhoCompleto}.${index}`);
                        }
                      });
                    } else {
                      // É um objeto aninhado
                      coletarErros(valor, caminhoCompleto);
                    }
                  }
                }
              });
            };

            coletarErros(errors);

            // Formata a mensagem de erro
            let mensagemErro = "Por favor, preencha os seguintes campos obrigatórios:";
            if (camposFaltando.length > 0) {
              mensagemErro += "\n\n" + camposFaltando.map(campo => `• ${campo}`).join("\n");
            } else {
              mensagemErro = "Por favor, corrija os erros no formulário";
            }

            toast.error(mensagemErro, {
              duration: 8000, // Aumenta a duração para dar tempo de ler todos os campos
            });
          })} className="space-y-4">
            <Tabs value={abaAtiva} onValueChange={setAbaAtiva} className="w-full">
              <TabsList className={`grid w-full ${isMedico ? 'grid-cols-5' : 'grid-cols-4'}`}>
                <TabsTrigger value="pessoa">Pessoa</TabsTrigger>
                <TabsTrigger value="usuario">Usuário</TabsTrigger>
                {isMedico && <TabsTrigger value="medico">Médico</TabsTrigger>}
                <TabsTrigger value="enderecos">Endereços</TabsTrigger>
                <TabsTrigger value="configuracoes">Configurações</TabsTrigger>
              </TabsList>
              
              <TabsContent value="pessoa" className="space-y-4">
                <Pessoa form={form} formData={formData} readOnly={false} />
              </TabsContent>
              
              <TabsContent value="usuario" className="space-y-4">
                <Usuario 
                  form={form}
                  formData={formData} 
                  perfisAcesso={perfisAcesso}
                  tiposUsuario={tiposUsuario}
                  usuario={usuario}
                  readOnly={false}
                  onTipoUsuarioChange={(tipoUsuarioId) => {
                    // Quando o tipo de usuário mudar, verifica se é médico
                    const tipoSelecionado = tiposUsuario.find(t => t.id.toString() === tipoUsuarioId);
                    if (tipoSelecionado?.codigo === 'MEDICO') {
                      setAbaAtiva("medico");
                    }
                  }}
                />
              </TabsContent>

              {isMedico && (
                <TabsContent value="medico" className="space-y-4">
                  <Medico 
                    medicoData={medicoData}
                    setMedicoData={setMedicoData}
                    especialidades={especialidades}
                    readOnly={false}
                    usuarioId={usuario?.id}
                  />
                </TabsContent>
              )}
              
              <TabsContent value="enderecos" className="space-y-4">
                <EnderecoComponent
                  form={form}
                  enderecos={formData.enderecos || []}
                  adicionarEndereco={adicionarEndereco}
                  removerEndereco={removerEndereco}
                  atualizarEndereco={atualizarEndereco}
                  atualizarEnderecoCompleto={atualizarEnderecoCompleto}
                  readOnly={false}
                />
              </TabsContent>
              
              <TabsContent value="configuracoes" className="space-y-4">
                <Configuracoes 
                  form={form}
                  ativo={formData.ativo}
                  readOnly={false}
                />
              </TabsContent>
            </Tabs>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Salvando..." : usuario ? "Atualizar" : "Salvar"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default UsuariosEdit;
